import React, { useState, useEffect } from "react";
import "./PartialPayment.css";
import axiosInstance from "../../../Config/axiosInstance";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat } from "react-number-format";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

//Setăm limba română pentru DatePicker.
registerLocale("ro", ro);

const EditPartialPayment = ({
  isOpen,
  onClose,
  onUpdatePayment,
  payment,
  invoiceNumber,
  remainingPayment,
  invoiceCurrency,
}) => {

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(null);
  const [error, setError] = useState("");
  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
  };

  /*
    Când se deschide popup-ul și există o plată selectată:
    - populăm formularul cu valorile existente
    - convertim data primită din backend într-un obiect Date
    Când popup-ul se închide:
    - curățăm formularul
   */
  useEffect(() => {
    if (isOpen && payment) {
      setAmount(payment.amount ?? "");

      if (payment.date) {
        const [year, month, day] = payment.date.split("-");
        setDate(new Date(Number(year), Number(month) - 1, Number(day)));
      } else {
        setDate(null);
      }

      setError("");
    }

    if (!isOpen) {
      setAmount("");
      setDate(null);
      setError("");
    }
  }, [isOpen, payment]);

  /*
    Validează și salvează modificările plății.
    La edit, nu comparăm doar cu remainingPayment, pentru că utilizatorul are voie să păstreze și suma curentă a plății.
    De aceea: maxAllowed = remainingPayment + valoarea plății curente
   */
  const handleSubmit = async () => {
    const isoDate = date ? date.toISOString().split("T")[0] : "";

    if (amount === "" || !isoDate) {
      setError("All fields are required.");
      return;
    }

    const numericAmount = parseFloat(amount);

    // la edit, trebuie să permiți inclusiv valoarea curentă + diferența rămasă
    const currentPaymentAmount = parseFloat(payment?.amount || 0);
    const maxAllowed = parseFloat(remainingPayment || 0) + currentPaymentAmount;

    if (numericAmount > maxAllowed) {
      setError(
        `Amount exceeds the remaining payment (${maxAllowed} ${invoiceCurrency || ""}).`
      );
      return;
    }

    const updatedPayment = {
      ...payment,
      amount,
      date: isoDate,
    };

    try {
      const response = await axiosInstance.put(
        `/payments-commercial/${payment.id}/`,
        updatedPayment
      );

      onUpdatePayment(response.data);
      setAmount("");
      setDate(null);
      setError("");
      onClose();
    } catch (error) {
      console.error("Error updating payment:", error.response?.data || error.message);
      setError("Error updating payment.");
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="partialpayment-dialog"
    >
      {/* Titlul popup-ului */}
      <DialogTitle>Edit Payment</DialogTitle>

      <DialogContent dividers>
        <form
          className="payment-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Numărul facturii este doar pentru informare */}
          <div className="payment-inputGroup">
            <label className="payment-label">Invoice Number</label>
            <input
              className="payment-input"
              type="text"
              value={invoiceNumber || ""}
              readOnly
            />
          </div>

          {/* Câmpul pentru suma plății */}
          <div className="payment-inputGroup">
            <label className="payment-label">
              Amount {invoiceCurrency ? `(${invoiceCurrency})` : ""}
              <span className="payment-required-star">*</span>
            </label>

            <NumericFormat
              className="payment-input"
              value={amount}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              allowLeadingZeros={false}
              inputMode="decimal"
              placeholder="0,00"
              onValueChange={({ floatValue }) => {
                setAmount(floatValue ?? "");
              }}
            />
          </div>

          {/* Câmpul pentru data plății */}
          <div className="payment-inputGroup">
            <label className="payment-label">
              Date<span className="payment-required-star">*</span>
            </label>

            <div className="datepicker-container invoicesregister-datepicker">
              <DatePicker
                selected={date}
                onChange={handleDateChange}
                dateFormat="dd.MM.yyyy"
                locale="ro"
                placeholderText="dd.mm.yyyy"
                className="datepicker-input"
                wrapperClassName="datepicker-wrapper"
              />
              <span className="calendar-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
            </div>
          </div>

          {/* Mesaj de eroare dacă validarea sau salvarea eșuează */}
          {error && <p className="payment-error">{error}</p>}
        </form>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        {/* Închide popup-ul fără salvare */}
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>

        {/* Salvează modificările */}
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPartialPayment;